import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { FormBuilder, FormGroup } from '@angular/forms';
import { JobLevels } from 'src/app/shared/dw-column';

@Component({
    selector: 'app-job-level-dropdown',
    templateUrl: './job-level-dropdown.component.html',
    styleUrls: ['./job-level-dropdown.component.scss'],
    standalone: false
})
export class JobLevelDropdownComponent implements OnChanges, OnInit {
  @Input() preSelectedJobLevels: string[] | undefined = [];
  @Input() multiSelect: boolean = false;
  @Input() useChipDisplay: boolean = false;
  @Output() jobLevelSelectionChanged = new EventEmitter<string[]>();
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  availableJobLevels: string[] = this.jobLevelsAsArray();
  form: FormGroup;
  private lastEmittedValue: string[] = [];

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      selectedLevels: [this.preSelectedJobLevels || this.availableJobLevels],
    });
    this.lastEmittedValue = this.form.get('selectedLevels')?.value;
  }

  // This component is always init'd with preselected set to undefined, but then the parent component will set it
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.preSelectedJobLevels && !changes.preSelectedJobLevels.firstChange) {
      const newPreSelectedJobLevels = changes.preSelectedJobLevels.currentValue;

      if (newPreSelectedJobLevels) {
        this.form.get('selectedLevels')?.setValue(newPreSelectedJobLevels, { emitEvent: false });
        this.lastEmittedValue = this.form.get('selectedLevels')?.value;
      }
    }
  }

  // When the menu is closed, emit the value if it has changed
  onSelectionChange(value: any) {
    if (this.hasSelectionChanged(this.lastEmittedValue, value)) {
      this.jobLevelSelectionChanged.emit(value);
      this.lastEmittedValue = value;
    }
  }

  onDropdownClosed() {
    // In form field mode, convert empty to "all selected" for better UX
    if (
      this.form.get('selectedLevels')?.value === undefined ||
      this.form.get('selectedLevels')?.value.length === 0
    ) {
      this.form.get('selectedLevels')?.setValue(this.availableJobLevels);
    }

    this.onSelectionChange(this.form.get('selectedLevels')?.value);
  }

  onMenuClosed() {
    // In chip mode, allow empty arrays (means "no filter")
    // In form field mode, convert empty to "all selected" for better UX
    if (
      !this.useChipDisplay &&
      (this.form.get('selectedLevels')?.value === undefined ||
        this.form.get('selectedLevels')?.value.length === 0)
    ) {
      this.form.get('selectedLevels')?.setValue(this.availableJobLevels);
    }

    this.onSelectionChange(this.form.get('selectedLevels')?.value);
  }

  selectAll() {
    this.form.get('selectedLevels')?.setValue(this.availableJobLevels);
  }

  unselectAll() {
    const emptyValue: string[] = [];
    this.form.get('selectedLevels')?.setValue(emptyValue);
  }

  clearSelection() {
    // Clear the filter by setting to empty array (show all, no filter)
    const emptyValue: string[] = [];
    this.form.get('selectedLevels')?.setValue(emptyValue);
    this.onSelectionChange(emptyValue);
  }

  areAllSelected() {
    return this.form.get('selectedLevels')?.value?.length === this.availableJobLevels.length;
  }

  areNoneSelected() {
    return (
      this.form.get('selectedLevels')?.value === undefined ||
      this.form.get('selectedLevels')?.value.length === 0
    );
  }

  applyAndClose() {
    this.onSelectionChange(this.form.get('selectedLevels')?.value);
    this.menuTrigger?.closeMenu();
  }

  getCollapsedStringValue(): string {
    if (this.form.get('selectedLevels')?.value === undefined) {
      return 'All';
    }

    if (this.form.get('selectedLevels')?.value.length === 0) {
      return 'All';
    }

    if (this.form.get('selectedLevels')?.value.length === this.availableJobLevels.length) {
      return 'All';
    }

    return this.form.get('selectedLevels')?.value.join(', ');
  }

  get isFilterActive(): boolean {
    const selectedLevels = this.form.get('selectedLevels')?.value;
    return !!(
      selectedLevels &&
      selectedLevels.length > 0 &&
      selectedLevels.length < this.availableJobLevels.length
    );
  }

  get chipText(): string {
    const selectedLevels = this.form.get('selectedLevels')?.value;
    if (!selectedLevels || selectedLevels.length === 0) {
      return '';
    }

    const count = selectedLevels.length;
    const firstLevel = selectedLevels[0];

    if (count === 1) {
      return firstLevel;
    }

    return `${firstLevel} + ${count - 1} more`;
  }

  // Helper function to compare arrays (lastEmitted vs current selection)
  private hasSelectionChanged(lastEmitted: string[], current: string[]): boolean {
    if (lastEmitted.length !== current.length) {
      return true;
    }

    return lastEmitted.some((item) => !current.includes(item));
  }

  // Convert job levels to an array
  jobLevelsAsArray(): string[] {
    return Object.values(JobLevels);
  }
}
